/**
 * My Requests Page
 * Lists all requests for a yacht owner
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { requestsAPI, chatAPI } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import {
  MapPin,
  Calendar,
  Clock,
  ChevronRight,
  Filter,
  PlusCircle,
  Trash2,
  User,
  Star,
  MessageCircle,
  Edit3,
  X,
  Briefcase,
  UserPlus,
} from 'lucide-react';
import { format, isPast, parseISO, startOfDay } from 'date-fns';

function MyRequests() {
  const navigate = useNavigate();
  const { showToast } = useNotifications();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [deleting, setDeleting] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  useEffect(() => {
    loadRequests();
  }, [filter]);

  const handleDelete = async (e, requestId) => {
    e.preventDefault(); // Prevent navigation
    e.stopPropagation();

    if (!window.confirm('Are you sure you want to delete this request?')) return;

    setDeleting(requestId);
    try {
      await requestsAPI.deleteRequest(requestId);
      showToast('Request deleted', 'success');
      loadRequests();
    } catch (error) {
      showToast(error.message || 'Failed to delete request', 'error');
    } finally {
      setDeleting(null);
    }
  };

  const handleCancel = async (e, requestId) => {
    e.preventDefault();
    e.stopPropagation();

    if (!window.confirm('Are you sure you want to cancel this request? The helper will be notified.')) return;

    setCancelling(requestId);
    try {
      await requestsAPI.cancelRequest(requestId);
      showToast('Request cancelled', 'success');
      loadRequests();
    } catch (error) {
      showToast(error.message || 'Failed to cancel request', 'error');
    } finally {
      setCancelling(null);
    }
  };

  const handleModify = (e, requestId) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/owner/requests/${requestId}/edit`);
  };

  const handleChatWithHelper = async (e, helperId) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const response = await chatAPI.getOrCreateConversation(helperId);
      const conversationId = response.data?.id || response.conversation?.id;
      navigate(`/messages/${conversationId}`);
    } catch (error) {
      showToast('Failed to open chat', 'error');
    }
  };

  const loadRequests = async () => {
    setLoading(true);
    try {
      const status = filter !== 'all' ? filter : undefined;
      const response = await requestsAPI.getMyRequests(status);
      // API returns { success: true, data: [...] }
      let requestsList = response.data || response.requests || [];

      // Filter out past requests (unless completed or cancelled - those should show)
      requestsList = requestsList.filter(r => {
        if (r.status === 'completed' || r.status === 'cancelled') return true;
        const requestDate = parseISO(r.scheduled_date);
        return !isPast(startOfDay(requestDate)) || format(requestDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
      });

      setRequests(requestsList);
    } catch (error) {
      console.error('Failed to load requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      active: 'bg-yellow-100 text-yellow-800',
      matched: 'bg-green-100 text-green-800',
      confirmed: 'bg-green-100 text-green-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pending',
      active: 'Pending',
      matched: 'Helper Selected',
      confirmed: 'Confirmed',
      in_progress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
    };
    return labels[status] || status.replace('_', ' ');
  };

  const isActiveRequest = (status) => {
    return ['pending', 'active', 'matched', 'in_progress'].includes(status);
  };

  const filterTabs = [
    { key: 'all', label: 'All' },
    { key: 'pending', label: 'Pending' },
    { key: 'matched', label: 'Helper Selected' },
    { key: 'in_progress', label: 'In Progress' },
    { key: 'completed', label: 'Completed' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-navy-900">
            My Requests
          </h1>
          <p className="text-navy-600 mt-1">
            Track and manage your mooring requests
          </p>
        </div>
        <Link
          to="/owner/request/new"
          className="mt-4 sm:mt-0 btn-primary inline-flex items-center"
        >
          <PlusCircle className="h-5 w-5 mr-2" />
          New Request
        </Link>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center space-x-2 overflow-x-auto pb-2">
        <Filter className="h-4 w-4 text-navy-400 flex-shrink-0" />
        {filterTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              filter === tab.key
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-navy-600 hover:bg-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Requests List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner w-8 h-8" />
        </div>
      ) : requests.length === 0 ? (
        <div className="card text-center py-12">
          <MapPin className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-navy-900 mb-2">
            No requests found
          </h3>
          <p className="text-navy-600 mb-6">
            {filter === 'all'
              ? "You haven't created any requests yet"
              : `No ${filter.replace('_', ' ')} requests`}
          </p>
          {filter === 'all' && (
            <Link to="/owner/request/new" className="btn-primary">
              Create Your First Request
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <Link
              key={request.id}
              to={`/owner/requests/${request.id}`}
              className="card block hover:shadow-luxury-lg transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 flex-wrap gap-y-2">
                    {request.request_category === 'position' ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gold-100 text-gold-800">
                        <UserPlus className="h-3.5 w-3.5 mr-1" />
                        Crew
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                        <MapPin className="h-3.5 w-3.5 mr-1" />
                        Mooring
                      </span>
                    )}
                    <h3 className="font-medium text-navy-900 text-lg">
                      {request.request_category === 'position' && request.crew_position
                        ? request.crew_position.replace(/_/g, ' ').replace(/^(deck|interior|galley|eng)\s/i, '').replace(/\b\w/g, l => l.toUpperCase())
                        : request.port_name}
                    </h3>
                    <span className={`badge ${getStatusBadge(request.status)}`}>
                      {getStatusLabel(request.status)}
                    </span>
                  </div>

                  {/* Position request - show department and location */}
                  {request.request_category === 'position' && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {request.crew_department && (
                        <span className="inline-flex items-center text-xs bg-gold-50 text-gold-700 px-2 py-1 rounded-full">
                          <Briefcase className="h-3 w-3 mr-1" />
                          {request.crew_department.charAt(0).toUpperCase() + request.crew_department.slice(1)}
                        </span>
                      )}
                      {request.port_name && (
                        <span className="inline-flex items-center text-xs bg-navy-50 text-navy-600 px-2 py-1 rounded-full">
                          <MapPin className="h-3 w-3 mr-1" />
                          {request.port_name}
                        </span>
                      )}
                    </div>
                  )}

                  <div className="mt-3 flex flex-wrap gap-4 text-sm text-navy-600">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      {request.request_category === 'position' && request.start_date
                        ? `${format(new Date(request.start_date), 'MMM d')} - ${request.end_date ? format(new Date(request.end_date), 'MMM d, yyyy') : 'Ongoing'}`
                        : format(new Date(request.scheduled_date), 'EEEE, MMM d, yyyy')}
                    </div>
                    {request.request_category !== 'position' && request.scheduled_time && (
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {request.scheduled_time}
                      </div>
                    )}
                    {request.request_category === 'position' && request.salary && (
                      <div className="flex items-center font-medium text-green-600">
                        {request.salary_currency === 'EUR' ? '€' : '$'}{request.salary}/month
                      </div>
                    )}
                  </div>

                  {request.yacht_name && (
                    <p className="mt-2 text-sm text-navy-500">
                      Yacht: {request.yacht_name}
                    </p>
                  )}

                  {request.request_category !== 'position' && request.helpers_needed > 1 && (
                    <p className="mt-1 text-sm text-navy-500">
                      {request.helpers_needed} helpers needed
                    </p>
                  )}
                </div>

                <div className="flex items-center space-x-2 flex-shrink-0">
                  {/* Modify and Cancel buttons for active requests */}
                  {isActiveRequest(request.status) && (
                    <>
                      <button
                        onClick={(e) => handleModify(e, request.id)}
                        className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Modify request"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => handleCancel(e, request.id)}
                        disabled={cancelling === request.id}
                        className="p-2 text-orange-500 hover:bg-orange-50 rounded-lg transition-colors"
                        title="Cancel request"
                      >
                        {cancelling === request.id ? (
                          <span className="spinner h-4 w-4" />
                        ) : (
                          <X className="h-4 w-4" />
                        )}
                      </button>
                    </>
                  )}
                  <button
                    onClick={(e) => handleDelete(e, request.id)}
                    disabled={deleting === request.id}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete request"
                  >
                    {deleting === request.id ? (
                      <span className="spinner h-4 w-4" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                  <ChevronRight className="h-5 w-5 text-navy-400" />
                </div>
              </div>

              {/* Applications indicator for pending */}
              {(request.status === 'pending' || request.status === 'active') && request.application_count > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-sm text-primary-600 font-medium">
                    {request.application_count} helper{request.application_count !== 1 ? 's' : ''} applied
                  </p>
                </div>
              )}

              {/* Helper info for matched/completed requests */}
              {request.helper && (request.status === 'matched' || request.status === 'completed' || request.status === 'in_progress') && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                        <User className="h-4 w-4 text-primary-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-navy-900">
                          {request.helper.first_name} {request.helper.last_name}
                        </p>
                        <div className="flex items-center text-xs text-navy-500">
                          {request.helper.average_rating > 0 && (
                            <span className="flex items-center mr-2">
                              <Star className="h-3 w-3 text-gold-500 mr-0.5" />
                              {request.helper.average_rating.toFixed(1)}
                            </span>
                          )}
                          <span>{request.helper.total_jobs || 0} jobs</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      {/* Show rating given for completed requests */}
                      {request.status === 'completed' && request.owner_given_rating && (
                        <span className="flex items-center text-xs bg-gold-50 text-gold-700 px-2 py-1 rounded-full">
                          <Star className="h-3 w-3 text-gold-500 fill-current mr-1" />
                          You rated: {request.owner_given_rating}/5
                        </span>
                      )}
                      {/* For completed requests, show Chat button */}
                      {request.status === 'completed' && (
                        <button
                          onClick={(e) => handleChatWithHelper(e, request.helper.id)}
                          className="flex items-center text-sm text-primary-600 hover:text-primary-700 bg-primary-50 px-3 py-1.5 rounded-lg"
                        >
                          <MessageCircle className="h-4 w-4 mr-1" />
                          Chat
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default MyRequests;
